/**
 * WP-50 — what one cell shows, what label it carries, and what its hover text
 * says (plan 8.1; plan 11 "Global": every number shows its label — extracted,
 * derived, inferred — and its provenance on hover; G7, G8).
 *
 * The label rule, in one place:
 *   - a formula cell shows its RECOMPUTED value and is derived (the engine
 *     computed it here, with formulaInputs 'recompute', plan 6.3);
 *   - a formula the engine cannot evaluate falls back to the workbook's cached
 *     value, which is extracted, exactly as plan 6.3 requires ("the cached
 *     value is used and labelled extracted; the cell is never guessed");
 *   - a cell a person edited in this session is derived;
 *   - anything else carries the label the payload gave it, and extracted when
 *     the payload gave none. The grid never produces `inferred`; only a parser
 *     that inferred something may say so, and it says so in the payload.
 */
import {
  EngineResult,
  EngineValue,
  WorkbookContext,
  cellCachedValue,
  cellHasFormula,
  cellStoredValue,
  isErrorBox,
} from './engineBridge';
import { FormattedValue, formatValue, valueToText } from './format';
import { GridCell, ValueLabel, WsChange } from './types';

export interface CellDisplay {
  /** Formatted for the screen. */
  text: string;
  align: 'left' | 'right';
  label: ValueLabel;
  /**
   * What the number-format reader applied. The formatting is itself a DERIVED
   * rendering of the value — it never changes the value's own label, and the
   * hover text says so whenever a format was applied.
   */
  format: FormattedValue['applied'];
  /** Unformatted, as a change record would carry it (plan 5.8). */
  value: string | null;
  formula: string | null;
  provenance: string;
  title: string;
  state: 'empty' | 'value' | 'formula' | 'unsupported' | 'error';
  isNumeric: boolean;
}

const EMPTY_DISPLAY: CellDisplay = {
  text: '',
  align: 'left',
  label: 'extracted',
  format: 'general',
  value: null,
  formula: null,
  provenance: '',
  title: '',
  state: 'empty',
  isNumeric: false,
};

function evaluate(ctx: WorkbookContext | null, sheet: string, ref: string): EngineResult | null {
  if (!ctx) return null;
  try {
    return ctx.evaluateCell(sheet, ref);
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    return {
      status: 'error',
      valueType: 'e',
      value: { __error: '#VALUE!' },
      numberValue: null,
      errorValue: '#VALUE!',
      unsupported: [],
      unsupportedFunctions: [],
      usedCachedInputs: [],
      reason: message,
      engineVersion: 'unavailable',
    };
  }
}

function provenanceText(sheet: string, ref: string, cell: GridCell, edited: boolean): string {
  const parts: string[] = [sheet + '!' + ref];
  if (cell.id !== null && cell.id !== undefined) parts.push('cell id ' + String(cell.id));
  if (typeof cell.formula === 'string' && cell.formula.length > 0) parts.push('formula ' + cell.formula);
  if (cell.numfmt) parts.push('format ' + String(cell.numfmt));
  if (cell.fill) parts.push('source fill ' + String(cell.fill));
  if (edited) parts.push('edited in this session');
  const extra = cell.provenance;
  if (typeof extra === 'string' && extra.length > 0) parts.push(extra);
  else if (extra && typeof extra === 'object') {
    Object.keys(extra).forEach((k) => {
      const v = (extra as Record<string, unknown>)[k];
      if (v !== null && v !== undefined && typeof v !== 'object') parts.push(k + ' ' + String(v));
    });
  }
  return parts.join(' · ');
}

function titleFor(display: CellDisplay, cell: GridCell, change: WsChange | null, reason: string | null): string {
  const lines: string[] = [];
  lines.push('Label: ' + display.label);
  lines.push('Provenance: ' + display.provenance);
  if (display.format === 'currency' || display.format === 'percent'
    || display.format === 'date' || display.format === 'number') {
    lines.push(
      'Format: derived — ' + display.format
      + (cell.numfmt ? ' from numfmt ' + String(cell.numfmt) : ' from the cell date flag'),
    );
  }
  if (reason) lines.push('Engine: ' + reason);
  if (cell.comment_text) {
    lines.push('Comment' + (cell.comment_author ? ' (' + String(cell.comment_author) + ')' : '') + ': ' + String(cell.comment_text));
  }
  if (change) {
    const shown = change.new_value !== null && change.new_value !== undefined ? String(change.new_value) : String(change.new_formula || '');
    lines.push(
      change.status.charAt(0).toUpperCase() + change.status.slice(1)
      + ' (' + change.source + '): ' + shown
      + (change.reason ? ' — ' + String(change.reason) : ''),
    );
  }
  return lines.join('\n');
}

/**
 * Compute everything the cell renderer and the side panel need for one cell.
 * `change` is the cta_ws_changes row painting the cell, when there is one: a
 * proposed row OVERLAYS its value (plan 8.1 fills for changed and proposed
 * cells), an accepted row only paints.
 */
export function describeCell(
  ctx: WorkbookContext | null,
  sheetName: string,
  ref: string,
  cell: GridCell | null,
  edited: boolean,
  change: WsChange | null,
): CellDisplay {
  if (!cell) {
    if (change && change.status === 'proposed') {
      const overlay = change.new_value !== null && change.new_value !== undefined
        ? String(change.new_value)
        : String(change.new_formula || '');
      const display: CellDisplay = {
        text: overlay,
        align: 'right',
        label: 'derived',
        format: 'text',
        value: overlay,
        formula: change.new_formula || null,
        provenance: sheetName + '!' + ref + ' · proposed by ' + change.source,
        title: '',
        state: 'value',
        isNumeric: true,
      };
      display.title = titleFor(display, {}, change, null);
      return display;
    }
    return EMPTY_DISPLAY;
  }

  let label: ValueLabel = 'extracted';
  let value: EngineValue = null;
  let state: CellDisplay['state'] = 'value';
  let reason: string | null = null;

  if (cellHasFormula(cell)) {
    const result = evaluate(ctx, sheetName, ref);
    if (result && result.status === 'ok') {
      value = result.value;
      label = 'derived';
      state = 'formula';
    } else if (result && result.status === 'error') {
      value = { __error: result.errorValue || '#VALUE!' };
      label = 'derived';
      state = 'error';
      reason = result.reason;
    } else {
      // Unsupported: the cached value, labelled extracted (plan 6.3).
      const cached = cellCachedValue(cell);
      reason = result ? result.reason : 'no engine context';
      if (cached === undefined) {
        value = '#UNSUPPORTED';
        label = 'derived';
        state = 'unsupported';
      } else {
        value = cached;
        label = 'extracted';
        state = 'unsupported';
      }
    }
  } else {
    value = cellStoredValue(cell);
    label = cell.label || 'extracted';
    state = value === null ? 'empty' : 'value';
  }

  if (edited) label = 'derived';

  const overlayProposed = change && change.status === 'proposed';
  let formatted = formatValue(value, cell.numfmt, cell.is_date);
  let valueText = valueToText(value);
  if (overlayProposed && change) {
    const overlay = change.new_value !== null && change.new_value !== undefined
      ? String(change.new_value)
      : String(change.new_formula || '');
    const asNumber = /^-?(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)$/.test(overlay.trim()) ? parseFloat(overlay.trim()) : null;
    formatted = asNumber === null
      ? { text: overlay, align: formatted.align, applied: 'text' }
      : formatValue(asNumber, cell.numfmt, cell.is_date);
    valueText = overlay.length === 0 ? null : overlay;
    label = 'derived';
  }

  const display: CellDisplay = {
    text: formatted.text,
    align: formatted.align,
    label: label,
    format: formatted.applied,
    value: valueText,
    formula: typeof cell.formula === 'string' && cell.formula.length > 0 ? cell.formula : null,
    provenance: provenanceText(sheetName, ref, cell, edited),
    title: '',
    state: state,
    isNumeric: typeof value === 'number' || isErrorBox(value),
  };
  display.title = titleFor(display, cell, change, reason);
  return display;
}

/**
 * The unformatted value of a cell as text — what old_value / new_value carry
 * in a cta_ws_changes row (plan 5.8).
 */
export function cellValueText(ctx: WorkbookContext | null, sheetName: string, ref: string, cell: GridCell | null): string | null {
  if (!cell) return null;
  if (cellHasFormula(cell)) {
    const result = evaluate(ctx, sheetName, ref);
    if (result && result.status === 'ok') return valueToText(result.value);
    if (result && result.status === 'error') return result.errorValue;
    const cached = cellCachedValue(cell);
    return cached === undefined ? null : valueToText(cached);
  }
  return valueToText(cellStoredValue(cell));
}

/** The text an editor opens with: the formula when there is one, else the raw value. */
export function editTextFor(cell: GridCell | null): string {
  if (!cell) return '';
  if (typeof cell.formula === 'string' && cell.formula.length > 0) {
    return cell.formula.charAt(0) === '=' ? cell.formula : '=' + cell.formula;
  }
  const stored = cellStoredValue(cell);
  if (stored === null) return '';
  if (typeof stored === 'number') return valueToText(stored) || '';
  if (typeof stored === 'boolean') return stored ? 'TRUE' : 'FALSE';
  if (isErrorBox(stored)) return stored.__error;
  return String(stored);
}
